import {
  HHAlert,
  HHButton,
  HHStack,
} from '@hinge-health/react-component-library';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Configuration, CreateConfigurationInput } from '../domain/types';
import {
  changedConfigurationFields,
  parseConfigurationPaste,
  toConfigurationFile,
} from '../utils/configuration-paste.utils';
import { stringifyJson } from '../utils/json.utils';
import { CONFIGURATION_PASTE_ERRORS_HEADING } from './configuration-json-paste-form';
import { CopyToClipboard } from './copy-to-clipboard';
import { JsonEditor } from './json-editor';

export const CONFIGURATION_JSON_EDIT_FORM_TEST_ID =
  'pdg-configuration-json-edit-form';
export const CONFIGURATION_JSON_EDIT_EDITOR_TEST_ID =
  'pdg-configuration-json-edit-editor';
export const CONFIGURATION_JSON_EDIT_ERRORS_TEST_ID =
  'pdg-configuration-json-edit-errors';
export const CONFIGURATION_JSON_EDIT_NO_CHANGES_TEST_ID =
  'pdg-configuration-json-edit-no-changes';
export const CONFIGURATION_JSON_EDIT_SUBMIT_TEST_ID =
  'pdg-configuration-json-edit-submit-button';
export const CONFIGURATION_JSON_EDIT_CANCEL_TEST_ID =
  'pdg-configuration-json-edit-cancel-button';
export const CONFIGURATION_JSON_EDIT_DISCARD_TEST_ID =
  'pdg-configuration-json-edit-discard-button';
export const CONFIGURATION_JSON_EDIT_COPY_TEST_ID =
  'pdg-configuration-json-edit-copy-button';
export const CONFIGURATION_JSON_EDIT_SERVER_ERROR_TEST_ID =
  'pdg-configuration-json-edit-server-error-alert';

export const CONFIGURATION_JSON_EDIT_LABEL = 'Configuration (JSON)';
export const CONFIGURATION_JSON_EDIT_HELPER_TEXT =
  'The whole configuration as { name, dataProvider, definition, actions }, the same shape as the files in partner-data-gateway-service/configurations/.';
export const CONFIGURATION_JSON_EDIT_COPY_LABEL = 'Copy configuration JSON';
export const CONFIGURATION_JSON_EDIT_NO_CHANGES_MESSAGE =
  'No changes to save: the JSON matches the stored configuration.';

export interface ConfigurationJsonEditFormProps {
  configuration: Configuration;
  /** Called only with a valid payload that differs from what is stored. */
  onSubmit: (
    values: CreateConfigurationInput,
    changedFields: (keyof CreateConfigurationInput)[],
  ) => void;
  onCancel: () => void;
  isSaving?: boolean;
  error?: string | null;
  /** Read-only: the JSON stays visible and copyable but cannot be saved. */
  readOnly?: boolean;
  /**
   * Reports whether the text differs from the stored configuration, so the
   * page can stop the operator switching away and losing it.
   */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * Edit a configuration as JSON.
 *
 * Seeded once from `configuration`; the page keys this component by id and
 * `updatedAt`, so a successful save (which refetches) re-seeds it with what
 * PDGS actually stored. The payload is sent verbatim as a full patch of all
 * four fields, with the same checks as the paste-to-create path.
 */
export const ConfigurationJsonEditForm = ({
  configuration,
  onSubmit,
  onCancel,
  isSaving = false,
  error = null,
  readOnly = false,
  onDirtyChange,
}: ConfigurationJsonEditFormProps): JSX.Element => {
  const stored = useMemo(
    () => toConfigurationFile(configuration),
    [configuration],
  );
  const initialText = useMemo(() => stringifyJson({ ...stored }), [stored]);

  const [text, setText] = useState(initialText);
  const [errors, setErrors] = useState<string[]>([]);
  const [noChanges, setNoChanges] = useState(false);

  const isDirty = text !== initialText;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const discard = (): void => {
    setText(initialText);
    setErrors([]);
    setNoChanges(false);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const result = parseConfigurationPaste(text);

    setErrors(result.errors);
    setNoChanges(false);

    if (!result.value) {
      return;
    }

    const changedFields = changedConfigurationFields(stored, result.value);

    // Re-formatting or re-ordering keys is not a change worth a write (or, on
    // a published configuration, a confirmation).
    if (changedFields.length === 0) {
      setNoChanges(true);

      return;
    }

    onSubmit(result.value, changedFields);
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      data-testid={CONFIGURATION_JSON_EDIT_FORM_TEST_ID}
    >
      <HHStack gap={3}>
        <HHStack direction="row" justifyContent="flex-end">
          <CopyToClipboard
            data={text}
            label={CONFIGURATION_JSON_EDIT_COPY_LABEL}
            testId={CONFIGURATION_JSON_EDIT_COPY_TEST_ID}
          />
        </HHStack>

        <JsonEditor
          label={CONFIGURATION_JSON_EDIT_LABEL}
          helperText={CONFIGURATION_JSON_EDIT_HELPER_TEXT}
          name="configuration"
          testId={CONFIGURATION_JSON_EDIT_EDITOR_TEST_ID}
          value={text}
          readOnly={readOnly}
          disabled={isSaving}
          onChange={(value): void => {
            setText(value);
            setErrors([]);
            setNoChanges(false);
          }}
        />

        {errors.length > 0 && (
          <HHAlert
            hhVariant="variant-bypass"
            variant="outlined"
            severity="error"
            data-testid={CONFIGURATION_JSON_EDIT_ERRORS_TEST_ID}
          >
            {CONFIGURATION_PASTE_ERRORS_HEADING}
            <ul>
              {errors.map(message => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </HHAlert>
        )}

        {noChanges && (
          <HHAlert
            hhVariant="variant-bypass"
            variant="outlined"
            severity="info"
            data-testid={CONFIGURATION_JSON_EDIT_NO_CHANGES_TEST_ID}
          >
            {CONFIGURATION_JSON_EDIT_NO_CHANGES_MESSAGE}
          </HHAlert>
        )}

        {error && (
          <HHAlert
            hhVariant="variant-bypass"
            variant="outlined"
            severity="error"
            data-testid={CONFIGURATION_JSON_EDIT_SERVER_ERROR_TEST_ID}
          >
            {error}
          </HHAlert>
        )}

        <HHStack direction="row" gap={2} justifyContent="flex-end">
          {isDirty && !readOnly && (
            <HHButton
              hhVariant="variant-bypass"
              variant="text"
              type="button"
              disabled={isSaving}
              onClick={discard}
              data-testid={CONFIGURATION_JSON_EDIT_DISCARD_TEST_ID}
            >
              Discard changes
            </HHButton>
          )}
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            type="button"
            disabled={isSaving}
            onClick={onCancel}
            data-testid={CONFIGURATION_JSON_EDIT_CANCEL_TEST_ID}
          >
            Cancel
          </HHButton>
          <HHButton
            hhVariant="variant-bypass"
            variant="contained"
            type="submit"
            // Untouched text can't be a change, so don't offer a save.
            disabled={readOnly || isSaving || !isDirty}
            data-testid={CONFIGURATION_JSON_EDIT_SUBMIT_TEST_ID}
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </HHButton>
        </HHStack>
      </HHStack>
    </form>
  );
};
